/**
 * EVENTSFLO-PROJECT-CREATION-CORE-UX-BLOCKERS-FIX-V1
 *
 * QuickAddVenueDialog — minimal inline dialog for creating a new Venue
 * from within another form (e.g. Project creation) without leaving the
 * parent flow. Keeps only the essentials the user needs to unblock:
 *
 *   name (required), address (required), city, country, postcode, notes
 *
 * On successful create, invokes `onCreated(newVenue)` so the parent can
 * refresh its venue list and auto-select the newly-created row.
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { createVenue, extractErrorMessage } from "../../lib/api";
import { CountrySelect } from "./CountrySelect";

const EMPTY = {
  name: "",
  address: "",
  address_line_2: "",
  city: "",
  country: "",
  postcode: "",
  notes: "",
  // EVENTSFLO-LIVE-DATA-ENTRY-VENUE-LOCATION-FILENAME-ADDRESS-UX-V1:
  // Default TRUE — if this venue only has one Location, create it
  // automatically from the same details so the operator doesn't need
  // to re-enter it.
  alsoCreateLocation: true,
};

export function QuickAddVenueDialog({ open, onOpenChange, onCreated, clientId }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const handleClose = (value) => {
    if (!value) {
      setForm(EMPTY);
    }
    onOpenChange(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Venue name is required");
      return;
    }
    if (!form.address.trim()) {
      toast.error("Address is required");
      return;
    }
    try {
      setSaving(true);
      // Attach client_id so the venue is scoped to the current client
      // when the project's client is already known. If no client is
      // selected yet the venue is created tenant-scoped only (the
      // backend stamps company_id from the caller's session).
      const payload = {
        name: form.name.trim(),
        address: form.address.trim(),
        address_line_2: form.address_line_2.trim() || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
        postcode: form.postcode.trim() || null,
        notes: form.notes.trim() || null,
      };
      if (clientId) payload.client_id = clientId;
      const res = await createVenue(payload, { alsoCreateLocation: form.alsoCreateLocation });
      const venue = res.data || res;
      toast.success(
        form.alsoCreateLocation
          ? `Venue "${venue.name}" and matching Location created`
          : `Venue "${venue.name}" created`
      );
      if (onCreated) onCreated(venue);
      handleClose(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="quick-add-venue-dialog"
      >
        <DialogHeader>
          <DialogTitle>Add new Venue</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="quick-venue-name">Name *</Label>
            <Input
              id="quick-venue-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Venue name"
              data-testid="quick-add-venue-name"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="quick-venue-address">Address Line 1 *</Label>
            <Input
              id="quick-venue-address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Street address"
              data-testid="quick-add-venue-address"
            />
          </div>
          <div>
            <Label htmlFor="quick-venue-address-line-2">Address Line 2</Label>
            <Input
              id="quick-venue-address-line-2"
              value={form.address_line_2}
              onChange={(e) => setForm({ ...form, address_line_2: e.target.value })}
              placeholder="Building / Floor / Suite (optional)"
              data-testid="quick-add-venue-address-line-2"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="quick-venue-postcode">Postcode</Label>
              <Input
                id="quick-venue-postcode"
                value={form.postcode}
                onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                data-testid="quick-add-venue-postcode"
              />
            </div>
            <div>
              <Label htmlFor="quick-venue-city">City</Label>
              <Input
                id="quick-venue-city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="quick-add-venue-city"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="quick-venue-country">Country</Label>
            {/* EVENTSFLO-UNIVERSAL-COUNTRY-DROPDOWN-AND-VENUE-ADDRESS-FIELDS-V1 */}
            <CountrySelect
              value={form.country}
              onValueChange={(v) => setForm({ ...form, country: v || "" })}
              valueMode="name"
              placeholder="Search country..."
              testId="quick-add-venue-country"
            />
          </div>
          <div>
            <Label htmlFor="quick-venue-notes">Notes</Label>
            <Textarea
              id="quick-venue-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="quick-add-venue-notes"
            />
          </div>
          {/* EVENTSFLO-LIVE-DATA-ENTRY-VENUE-LOCATION-FILENAME-ADDRESS-UX-V1:
              Default-checked auto-create Location option. Reduces duplicate
              entry when the venue has only one Location. */}
          <div className="flex items-start gap-2 rounded-md border border-dashed border-border/50 bg-muted/30 p-2">
            <input
              id="quick-venue-also-create-location"
              type="checkbox"
              className="mt-0.5"
              checked={form.alsoCreateLocation}
              onChange={(e) => setForm({ ...form, alsoCreateLocation: e.target.checked })}
              data-testid="quick-add-venue-also-create-location"
            />
            <div className="text-xs leading-tight">
              <Label
                htmlFor="quick-venue-also-create-location"
                className="cursor-pointer font-medium"
              >
                Create Location using same details
              </Label>
              <p className="mt-0.5 text-muted-foreground">
                If this Venue only has one Location, create it automatically using the same details.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={saving}
              data-testid="quick-add-venue-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="quick-add-venue-submit"
            >
              {saving ? "Saving…" : "Save Venue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default QuickAddVenueDialog;
